import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/ui/ProductCard';
import Button from '../components/ui/Button';
import { fetchProducts } from '../api/products';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedSize, setSelectedSize] = useState('All');
  const [showInStock, setShowInStock] = useState(true);
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [allSizes, setAllSizes] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8); // Number of products per page
  
  // Fetch products from the database
  useEffect(() => {
    const getProducts = async () => {
      try {
        setLoading(true);
        const data = await fetchProducts();
        setProducts(data);
        
        // Extract all unique sizes in cm
        const sizesSet = new Set();
        data.forEach(product => {
          product.sizes.forEach(size => {
            if (size.sizeInCm) {
              sizesSet.add(size.sizeInCm);
            }
          });
        });
        
        // Sort sizes numerically
        const sortedSizes = Array.from(sizesSet).sort((a, b) => a - b);
        
        setAllSizes(sortedSizes);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
        setLoading(false);
      }
    };
    
    getProducts();
  }, []);
  
  // Filter products by size and availability
  const filteredProducts = products.filter(product => {
    // Filter by availability
    if (showInStock && !showOutOfStock) {
      if (!product.inStock) return false;
    } else if (!showInStock && showOutOfStock) {
      if (product.inStock) return false;
    } else if (!showInStock && !showOutOfStock) {
      return false;
    }
    
    // Filter by size
    if (selectedSize === 'All') return true;
    
    return product.sizes.some(size => size.sizeInCm === selectedSize);
  });
  
  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'featured') {
      return (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0);
    } else if (sortBy === 'newest') {
      return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
    } else if (sortBy === 'price-low') {
      return a.price - b.price;
    } else if (sortBy === 'price-high') {
      return b.price - a.price;
    }
    return 0;
  });
  
  // Get current products for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  
  // Calculate total pages
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Go to next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  return (
    <Layout>
      {/* Hero Banner */}
      <div className="relative py-20 md:py-32">
        <div className="absolute inset-0 bg-black">
          <img 
            src="/images/collection.jpg" 
            alt="Our Collection" 
            className="w-full h-full object-cover object-center opacity-50"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-light mb-4 text-shadow">
            Our Collection
          </h1>
          <div className="w-24 h-1 bg-primary mx-auto mb-6"></div>
          <p className="text-light max-w-3xl mx-auto text-lg">
            Browse our collection of handcrafted hats made by skilled artisans in Bali.
          </p>
          <p className="text-primary/90 text-sm mt-4">
            All catalog prices are displayed in Indonesian Rupiah (IDR).
          </p>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12 relative">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img 
            src="/images/home-2.jpg" 
            alt="" 
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        </div>
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-light">Loading products...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="primary">
              Try Again
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:justify-between mb-12 border-b border-gray-800 pb-8">
              {/* Size Filter */}
              <div className="mb-6 md:mb-0">
                <h3 className="font-medium text-primary mb-4">Size</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSize('All')}
                    className={`px-4 py-2 text-sm transition-colors ${
                      selectedSize === 'All'
                        ? 'bg-primary text-dark'
                        : 'bg-secondary text-light hover:bg-gray-800'
                    }`}
                  >
                    All Sizes
                  </button>
                  {allSizes.map(size => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 text-sm transition-colors ${
                        selectedSize === size
                          ? 'bg-primary text-dark'
                          : 'bg-secondary text-light hover:bg-gray-800'
                      }`}
                    >
                      {size}cm
                    </button>
                  ))}
                </div>
                
                {/* Availability Filter */}
                <h3 className="font-medium text-primary mb-4 mt-6">Availability</h3>
                <div className="flex gap-4">
                  <label className="flex items-center text-light">
                    <input
                      type="checkbox"
                      checked={showInStock}
                      onChange={() => setShowInStock(!showInStock)}
                      className="mr-2"
                    />
                    <span>In Stock</span>
                  </label>
                  <label className="flex items-center text-light">
                    <input
                      type="checkbox"
                      checked={showOutOfStock}
                      onChange={() => setShowOutOfStock(!showOutOfStock)}
                      className="mr-2"
                    />
                    <span>Out of Stock</span>
                  </label>
                </div>
              </div>
              
              {/* Sort Options */}
              <div>
                <h3 className="font-medium text-primary mb-4">Sort By</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-secondary text-light border border-gray-800 px-4 py-2 focus:outline-none focus:border-primary"
                >
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
            
            {/* Products Grid */}
            {sortedProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-light mb-4">No products match your filters.</p>
                <Button onClick={() => {
                  setSelectedSize('All');
                  setShowInStock(true);
                  setShowOutOfStock(true);
                }} variant="primary">
                  Reset Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {currentItems.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
            
            {/* Pagination - Only show if we have products and more than one page */}
            {sortedProducts.length > 0 && totalPages > 1 && (
              <div className="mt-16 flex justify-center">
                <div className="flex space-x-2">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="px-4"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  {/* Generate page number buttons */}
                  {[...Array(totalPages).keys()].map(number => (
                    <Button 
                      key={number + 1}
                      variant={currentPage === number + 1 ? "primary" : "secondary"} 
                      size="sm" 
                      className="px-4"
                      onClick={() => paginate(number + 1)}
                    >
                      {number + 1}
                    </Button>
                  ))}
                  
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="px-4"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Products;
